import { apiClient, OAUTH_BINDING_API_BASE } from '../../services/apiClient';
import type {
  AmazonSellerAccount,
  AuthorizationUrlResponse,
  ListAccountsResponse,
  MarketplaceCode,
} from './types';

/**
 * Amazon認証URLを取得
 * @param marketplace マーケットプレイスコード（デフォルト: JP）
 * @returns 認証URLとstate
 */
export async function getAuthorizationUrl(marketplace: MarketplaceCode = 'JP'): Promise<AuthorizationUrlResponse> {
  const { data } = await apiClient.get<AuthorizationUrlResponse>('/amazon/auth/authorize', {
    params: { marketplace },
    // 結合 Cookie を first-party にするため同一オリジン経由 (apiClient.ts 参照)
    baseURL: OAUTH_BINDING_API_BASE,
  });
  return data;
}

/**
 * Amazon 商品一覧を取得
 *
 * レスポンス形は呼び出し側で参照するキーが異なるため素の形で返す (`{ products: [...] }` 等)。
 * NOTE: 同じ生 apiClient 呼び出しが pages/products 等にも残っており、そちらは別タスク。
 */
export async function listAmazonProducts(params?: Record<string, unknown>): Promise<any> {
  const { data } = await apiClient.get('/amazon/products', { params });
  return data;
}

/**
 * Amazon 商品を ASIN 単体で取得。レスポンスは `{ product: {...} }` 形式。
 */
export async function getAmazonProduct(asin: string): Promise<any> {
  const { data } = await apiClient.get(`/amazon/products/${asin}`);
  return data;
}

/**
 * 登録済みのAmazon Seller Accountsを取得
 * @returns Amazonアカウント一覧
 */
export async function getAmazonAccounts(): Promise<AmazonSellerAccount[]> {
  const { data } = await apiClient.get<ListAccountsResponse>('/amazon/accounts');
  return data.accounts || [];
}

/**
 * Amazon Seller Accountを削除
 * @param accountId アカウントID
 */
export async function deleteAmazonAccount(accountId: string): Promise<void> {
  await apiClient.delete(`/amazon/accounts/${accountId}`);
}

/**
 * Amazon認証を開始（新しいウィンドウでOAuthフローを開く）
 * @param marketplace マーケットプレイスコード
 */
export async function initiateAmazonAuth(marketplace: MarketplaceCode = 'JP'): Promise<void> {
  const { authorization_url } = await getAuthorizationUrl(marketplace);

  // 新しいウィンドウでAmazon認証ページを開く
  window.location.href = authorization_url;
}

/**
 * Listings Items APIから商品詳細を取得してDBに保存
 * @param sku 商品SKU
 * @param asin 商品ASIN
 * @param accountId Amazon Seller Account ID
 * @returns 保存された商品情報
 */
export async function saveListing(sku: string, asin: string, accountId: string): Promise<any> {
  const { data } = await apiClient.post(`/amazon/listings/${sku}/save`, {
    account_id: accountId,
    asin: asin,
  });
  return data;
}
